/**
 * Mock Group Service
 *
 * In-memory mock that mirrors the real groupService API.
 * Swap this in to test the UI without a backend.
 *
 * Usage: In Groups.jsx, change imports from './groupService' to './mockGroupService'
 */

import { mockGroups, mockEmails } from '../data/mockData';

// Deep clone so we can mutate without affecting the original module-level data
let groups = JSON.parse(JSON.stringify(mockGroups));
let emails = JSON.parse(JSON.stringify(mockEmails));
let nextGroupId = 100;

const delay = (ms = 200) => new Promise(r => setTimeout(r, ms));

/**
 * Get list of groups with search and pagination
 */
export const getGroups = async (params = {}) => {
  await delay();
  const { search = '', page = 1, limit = 10 } = params;

  let filtered = groups;
  if (search) {
    const term = search.toLowerCase();
    filtered = groups.filter(g =>
      g.name.toLowerCase().includes(term) ||
      g.description.toLowerCase().includes(term)
    );
  }

  const total = filtered.length;
  const totalPages = Math.ceil(total / limit) || 1;
  const start = (page - 1) * limit;
  const data = filtered.slice(start, start + limit);

  return {
    data,
    pagination: { page, limit, total, totalPages }
  };
};

/**
 * Get group by ID
 */
export const getGroupById = async (groupId) => {
  await delay();
  const group = groups.find(g => g.id === groupId);
  if (!group) throw new Error('Group not found');
  return { ...group };
};

/**
 * Create new group
 */
export const createGroup = async (groupData) => {
  await delay(300);
  const newGroup = {
    id: nextGroupId++,
    name: groupData.name,
    description: groupData.description || '',
    emailCount: 0,
    childGroupCount: 0,
    createdDate: new Date().toISOString().split('T')[0],
    isActive: true,
    emails: [],
    childGroups: [],
  };
  groups.push(newGroup);
  return { data: newGroup };
};

/**
 * Update group
 */
export const updateGroup = async (groupId, groupData) => {
  await delay(300);
  const group = groups.find(g => g.id === groupId);
  if (!group) throw new Error('Group not found');
  group.name = groupData.name;
  group.description = groupData.description;
  return { data: group };
};

/**
 * Delete group
 */
export const deleteGroup = async (groupId) => {
  await delay(300);
  // Also remove this group from any parent's childGroups
  groups.forEach(g => {
    g.childGroups = g.childGroups.filter(id => id !== groupId);
    g.childGroupCount = g.childGroups.length;
  });
  groups = groups.filter(g => g.id !== groupId);
  return { success: true };
};

/**
 * Get emails in a group
 */
export const getGroupEmails = async (groupId) => {
  await delay();
  const group = groups.find(g => g.id === groupId);
  if (!group) throw new Error('Group not found');

  return group.emails
    .map(emailId => emails.find(e => e.id === emailId))
    .filter(Boolean)
    .map(e => ({
      id: e.id,
      email: e.email,
      name: e.name || '',
      verificationStatus: e.verificationStatus || 'pending',
      addedAt: e.dateAdded || ''
    }));
};

/**
 * Add emails to group
 */
export const addEmailsToGroup = async (groupId, emailIds) => {
  await delay(300);
  const group = groups.find(g => g.id === groupId);
  if (!group) throw new Error('Group not found');

  emailIds.forEach(id => {
    if (!group.emails.includes(id)) {
      group.emails.push(id);
    }
  });
  group.emailCount = group.emails.length;
  return { success: true };
};

/**
 * Remove email from group
 */
export const removeEmailFromGroup = async (groupId, emailId) => {
  await delay(200);
  const group = groups.find(g => g.id === groupId);
  if (!group) throw new Error('Group not found');

  group.emails = group.emails.filter(id => id !== emailId);
  group.emailCount = group.emails.length;
  return { success: true };
};

/**
 * Get child groups of a group
 */
export const getGroupChildGroups = async (groupId) => {
  await delay();
  const group = groups.find(g => g.id === groupId);
  if (!group) throw new Error('Group not found');

  return group.childGroups
    .map(childId => groups.find(g => g.id === childId))
    .filter(Boolean)
    .map(g => ({
      id: g.id,
      name: g.name,
      description: g.description || '',
      emailCount: g.emailCount || 0,
      addedAt: g.createdDate || ''
    }));
};

/**
 * Add child groups to a group
 */
export const addChildGroupsToGroup = async (groupId, childGroupIds) => {
  await delay(300);
  const group = groups.find(g => g.id === groupId);
  if (!group) throw new Error('Group not found');

  childGroupIds.forEach(id => {
    if (id === groupId) return; // prevent self-reference
    if (!group.childGroups.includes(id)) {
      group.childGroups.push(id);
    }
  });
  group.childGroupCount = group.childGroups.length;
  return { success: true };
};

/**
 * Remove child group from a group
 */
export const removeChildGroupFromGroup = async (groupId, childGroupId) => {
  await delay(200);
  const group = groups.find(g => g.id === groupId);
  if (!group) throw new Error('Group not found');

  group.childGroups = group.childGroups.filter(id => id !== childGroupId);
  group.childGroupCount = group.childGroups.length;
  return { success: true };
};
